/**
 * Shared types for the Knowledge (formerly "Memory & Context") feature.
 *
 * The three tabs — Suppressions / Enforcements / Context — are projections of the same
 * backend `AnalysisMemory` chunk; they differ only in which fields are shown, so one
 * generic Row / List / Filters set is driven by per-tab card configs.
 *
 * Backend contract (asgard `AnalysisMemory`): a chunk exposes `content` (the rule
 * text), `category`, `filePattern`, `sourceFile`, `sourceType`, `status`, `scope`,
 * `isInferred`, `createdAt`, a free-form `metadata` blob, and the origin `repository`.
 * Producer-written extras (issue_code / issue_description / reason) live inside
 * `metadata`; there are no `convention` / `contentSummary` fields (those were FE-only).
 */
import {
  AnalysisMemory,
  AnalysisMemoryCategoryChoices,
  AnalysisMemoryChunkTypeChoices,
  AnalysisMemoryScopeChoices,
  AnalysisMemorySourceTypeChoices
} from '~/types/types'

export {
  AnalysisMemoryChunkTypeChoices as KnowledgeChunkType,
  AnalysisMemoryCategoryChoices as KnowledgeCategory,
  AnalysisMemorySourceTypeChoices as KnowledgeSourceType,
  AnalysisMemoryScopeChoices as KnowledgeScope
}

/**
 * A memory chunk = the generated backend type. We never invent fields on top of it;
 * per-tab projections are derived in the card configs (e.g. `metadata.issue_code`).
 */
export type KnowledgeChunk = AnalysisMemory

export interface KnowledgeMetadata {
  issue_code?: string | null
  issue_description?: string | null
  reason?: string | null
  promoted_at?: string | null
}

export function chunkMetadata(chunk: KnowledgeChunk): KnowledgeMetadata {
  return (chunk.metadata as KnowledgeMetadata | null | undefined) ?? {}
}

export function chunkOriginRepo(chunk: KnowledgeChunk): string | null {
  return chunk.repository?.name ?? null
}

const MATCH_ALL_PATTERNS = new Set(['', '*', '**', '**/*', '**/**'])
export function isMatchAllPattern(pattern: string | null | undefined): boolean {
  return MATCH_ALL_PATTERNS.has((pattern ?? '').trim())
}

export function filePatternLabel(pattern: string | null | undefined): string {
  return isMatchAllPattern(pattern) ? 'All files' : pattern ?? ''
}

export interface KnowledgeFilters {
  content: string | null
  source: AnalysisMemorySourceTypeChoices | null
  category: Array<AnalysisMemoryCategoryChoices>
  repository: KnowledgeFilterOption | null
}

export const DEFAULT_KNOWLEDGE_FILTERS: KnowledgeFilters = {
  content: null,
  source: null,
  category: [],
  repository: null
}

export function isFilterActive(filters: KnowledgeFilters): boolean {
  return (
    filters.content !== null ||
    filters.source !== null ||
    filters.category.length > 0 ||
    filters.repository !== null
  )
}

export const KNOWLEDGE_PAGE_SIZE = 20

// Literal values the backend memories(sort:) OrderingFilter accepts (plain String, not an enum).
export enum KnowledgeSort {
  SOURCE_FILE = 'source-file',
  SCOPE_PROMOTED_FIRST = 'scope-promoted-first',
  MODIFIED_DESC = 'modified-desc',
  ORIGIN_REPO = 'repository'
}

/**
 * Card render config for a Knowledge tab — rows render as cards (BaseCard) styled like
 * the Ignore Rules page. Each tab supplies one config; the generic card reads regions
 * from it, so adding a tab never touches the card component.
 */
export interface KnowledgeMetaItem {
  /** Icon shown before the value (optional); a fn to pick per-chunk (e.g. per category). */
  icon?: string | ((chunk: KnowledgeChunk) => string)
  /** Render the value in mono font (file patterns / globs); a fn to decide per-chunk. */
  mono?: boolean | ((chunk: KnowledgeChunk) => boolean)
  value: (chunk: KnowledgeChunk) => string
  /** When set, the meta value renders as a link to this path (e.g. origin repo); undefined skips the link. */
  href?: (chunk: KnowledgeChunk) => string | undefined
}

export interface KnowledgeCardConfig {
  primary?: (chunk: KnowledgeChunk) => string | null
  title: (chunk: KnowledgeChunk) => string
  body?: (chunk: KnowledgeChunk) => string | null
  meta: Array<KnowledgeMetaItem>
  groupBy?: (chunk: KnowledgeChunk) => string
}

export interface RenderedMeta {
  icon?: string
  mono?: boolean
  text: string
  href?: string
}

export interface RenderedRow {
  id: string
  primary: string
  title: string
  body: string
  promotedToOwner: boolean
  /** Promoted chunk whose origin repo was deleted (`repository == null`) — hides Demote. */
  originRepoMissing: boolean
  meta: Array<RenderedMeta>
  group: string
}

export interface KnowledgeFilterOption {
  value: string
  label: string
  icon?: string
}

export type KnowledgeOptionFetcher = (query: string) => Promise<Array<KnowledgeFilterOption>>

export interface KnowledgeFilterField {
  key: keyof KnowledgeFilters
  kind: 'search' | 'multi' | 'combobox'
  label: string
  icon?: string
  placeholder?: string
  options?: Array<KnowledgeFilterOption>
  /** For `combobox` fields: server-side search fn returning matching options. */
  fetchOptions?: KnowledgeOptionFetcher
}

export enum KnowledgeChunkModuleMutations {
  SET_CHUNKS = 'setChunks',
  SET_TOTAL_COUNT = 'setTotalCount',
  SET_LOADING = 'setLoading',
  SET_ERROR = 'setError'
}

export enum KnowledgeChunkModuleActions {
  FETCH = 'fetch',
  FETCH_REPO_OPTIONS = 'fetchRepoOptions',
  DELETE = 'delete',
  PROMOTE = 'promote',
  DEMOTE = 'demote'
}

export enum KnowledgeChunkModuleGetters {
  IS_EMPTY = 'isEmpty'
}

export interface KnowledgeChunkModuleState {
  chunks: Array<KnowledgeChunk>
  totalCount: number
  loading: boolean
  error: Error | null
}
